"use client";

import { c as _c } from "react/compiler-runtime";
import AlertCircleIcon from "@/assets/icons/alert-circle.svg";
import { cn, toInputValue } from "@/lib/utils";
import { Tooltip } from "@/shared/ui/tooltip/tooltip";
import { Skeleton } from "@mantine/core";
import * as React from "react";
import classes from "./definition-list.module.css";
export type DefinitionListItem = {
  /** Bold label on the left side — string or any node (e.g. platform icon) */
  label: React.ReactNode;
  /** Value rendered on the right side — can be a string, number, or any node */
  value: React.ReactNode;
  /** Optional icon rendered next to the label (14×14) — only used when label is a string */
  icon?: React.ReactNode;
  /** Tooltip text shown when hovering over the icon */
  tooltip?: string;
  /** When true the value cell renders as an editable inline input */
  editable?: boolean;
  /** When true the editable value renders as a textarea */
  multiline?: boolean;
  /** Unit/suffix shown after an editable value (e.g. "hours", "%") */
  suffix?: string;
  /** Optional max character length for editable inputs */
  maxLength?: number;
  /** Called with the new string value whenever the input changes */
  onChange?: (value: string) => void;
};
export type DefinitionListProps = {
  /** Array of key-value rows to display */
  items?: DefinitionListItem[];
  /** Shows skeleton rows while data is loading */
  isLoading?: boolean;
  /** Number of skeleton rows to show while loading */
  loadingRows?: number;
  /** Renders an empty-state message when items is empty */
  isEmpty?: boolean;
  /** Custom empty-state message */
  emptyMessage?: string;
  /** Renders an error state */
  error?: string | null;
  className?: string;
};

/** Suffix for read-only values; skipped when the value is empty/placeholder. */
function readOnlySuffix(suffix: string | undefined, value: React.ReactNode): string | null {
  if (!suffix) return null;
  if (value == null || value === "" || value === "-") return null;
  return /^[%°]/.test(suffix) ? suffix : ` ${suffix}`;
}
function SkeletonRow() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={classes.skeletonRow}><Skeleton className={classes.skeletonLabel} /><Skeleton className={classes.skeletonValue} /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Design-system key/value definition list (Figma StreamHub UI Kit).
 * Uses Mantine `Skeleton` for loading rows.
 */
export function DefinitionList(t0) {
  const $ = _c(24);
  const {
    items,
    isLoading: t1,
    loadingRows: t2,
    isEmpty,
    emptyMessage: t3,
    error,
    className
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const loadingRows = t2 === undefined ? 5 : t2;
  const emptyMessage = t3 === undefined ? "No details available" : t3;
  const derivedIsEmpty = isEmpty ?? (!isLoading && !error && (!items || items.length === 0));
  let t4;
  if ($[0] !== className) {
    t4 = cn(classes.root, className);
    $[0] = className;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== isLoading || $[3] !== loadingRows) {
    t5 = isLoading && Array.from({
      length: loadingRows
    }).map((_, i) => <React.Fragment key={i}><SkeletonRow />{i < loadingRows - 1 && <div className={classes.divider} />}</React.Fragment>);
    $[2] = isLoading;
    $[3] = loadingRows;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== error || $[6] !== isLoading) {
    t6 = !isLoading && error && <div className={classes.error}><AlertCircleIcon width={15} height={15} className="shrink-0" aria-hidden={true} /><span>{error}</span></div>;
    $[5] = error;
    $[6] = isLoading;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== derivedIsEmpty || $[9] !== emptyMessage || $[10] !== error || $[11] !== isLoading) {
    t7 = !isLoading && !error && derivedIsEmpty && <p className={classes.empty}>{emptyMessage}</p>;
    $[8] = derivedIsEmpty;
    $[9] = emptyMessage;
    $[10] = error;
    $[11] = isLoading;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== derivedIsEmpty || $[14] !== error || $[15] !== isLoading || $[16] !== items) {
    t8 = !isLoading && !error && !derivedIsEmpty && items?.map((item, i_0) => <React.Fragment key={i_0}><div className={cn(classes.row, item.multiline && classes.rowMultiline)}><div className={classes.labelCell}><span className={classes.label}>{item.label}</span>{item.icon && (item.tooltip ? <Tooltip content={item.tooltip}><span className={classes.iconButton}>{item.icon}</span></Tooltip> : <span className={classes.iconStatic}>{item.icon}</span>)}</div><div className={classes.valueCell}>{item.editable && item.multiline && <textarea className={cn(classes.input, classes.textarea)} rows={3} value={toInputValue(item.value)} maxLength={item.maxLength} onChange={e => item.onChange?.(e.target.value)} />}{item.editable && !item.multiline && <div className={cn(classes.inputWithSuffix, item.suffix && classes.inputWithSuffixActive)}><input type="text" className={classes.input} value={toInputValue(item.value)} maxLength={item.maxLength} onChange={e_0 => item.onChange?.(e_0.target.value)} />{item.suffix ? <span className={classes.suffix}>{item.suffix}</span> : null}</div>}{!item.editable && <div className={classes.valueWrap}><span className={cn(classes.value, item.multiline && classes.valueMultiline)}>{item.value}{readOnlySuffix(item.suffix, item.value)}</span></div>}</div></div>{i_0 < items.length - 1 && <div className={classes.divider} />}</React.Fragment>);
    $[13] = derivedIsEmpty;
    $[14] = error;
    $[15] = isLoading;
    $[16] = items;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
    t9 = <div data-slot="definition-list" className={t4}>{t5}{t6}{t7}{t8}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}